import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
    FaSave, FaTimes, FaUser, FaBuilding, FaFlag, FaCalendarAlt, 
    FaLanguage, FaRedo, FaInfoCircle, FaMobile, FaUserCheck,
    FaUserPlus, FaTrash, FaUsers, FaCheckCircle, FaPlus
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const EditTask = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [newAssignee, setNewAssignee] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        department: '',
        priority: 'Medium',
        frequency: 'One Time',
        language: 'english',
        dueDate: '',
        startDate: '',
        daysCount: '',
        dayOfWeek: 'Monday',
        weeksCount: '',
        dayOfMonth: '1',
        monthsCount: ''
    });

    useEffect(() => {
        fetchTaskDetails();
        fetchUsers();
        fetchDepartments();
    }, [taskId]);

    const fetchTaskDetails = async () => {
        try {
            const response = await axios.get(`/api/tasks/${taskId}/edit`);
            if (response.data.success) {
                const task = response.data.task;
                setFormData({
                    title: task.title || '',
                    description: task.description || '',
                    department: task.department || '',
                    priority: task.priority || 'Medium',
                    frequency: task.frequency || 'One Time',
                    language: task.language || 'english',
                    dueDate: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
                    startDate: task.start_date ? new Date(task.start_date).toISOString().split('T')[0] : '',
                    daysCount: task.days_count || '',
                    dayOfWeek: task.day_of_week || 'Monday',
                    weeksCount: task.weeks_count || '',
                    dayOfMonth: task.day_of_month || '1',
                    monthsCount: task.months_count || ''
                });
                
                // Load multiple assignees
                if (task.assignees && task.assignees.length > 0) {
                    setSelectedAssignees(task.assignees);
                } else if (task.assigned_to) {
                    // Handle old single assignee format
                    setSelectedAssignees([{
                        mobile: task.assigned_to,
                        name: task.assigned_to_name || task.assigned_name,
                        department: task.assigned_department
                    }]);
                }
            }
        } catch (error) {
            toast.error('Failed to load task details');
            navigate('/tasks');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get('/api/users');
            if (response.data && response.data.users) {
                setUsers(response.data.users);
            }
        } catch (error) {
            console.error('Failed to load users');
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await axios.get('/api/departments');
            if (response.data.success) {
                setDepartments(response.data.departments || []);
            }
        } catch (error) {
            console.error('Failed to load departments');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddAssignee = () => {
        if (!newAssignee) {
            toast.error('Please select a user to assign');
            return;
        }
        
        // Check if user already assigned
        if (selectedAssignees.some(a => a.mobile === newAssignee)) {
            toast.error('User already assigned to this task');
            return;
        }
        
        const selectedUser = users.find(u => u.mobile === newAssignee);
        if (selectedUser) {
            setSelectedAssignees(prev => [...prev, {
                mobile: selectedUser.mobile,
                name: selectedUser.name,
                department: selectedUser.department,
                email: selectedUser.email
            }]);
            setNewAssignee('');
            toast.success(`${selectedUser.name} added to task`);
        }
    };

    const handleRemoveAssignee = (mobile) => {
        const removedUser = selectedAssignees.find(a => a.mobile === mobile);
        setSelectedAssignees(prev => prev.filter(a => a.mobile !== mobile));
        toast.success(`${removedUser?.name} removed from task`);
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return false;
        }
        if (!formData.description.trim()) {
            toast.error('Description is required');
            return false;
        }
        if (selectedAssignees.length === 0) {
            toast.error('Please assign at least one user to this task');
            return false;
        }
        
        // Validate recurring task fields
        if (formData.frequency !== 'One Time') {
            if (!formData.startDate) {
                toast.error('Start date is required for recurring tasks');
                return false;
            }
            
            if (formData.frequency === 'Daily' && (!formData.daysCount || formData.daysCount < 1)) {
                toast.error('Please enter valid number of days');
                return false;
            }
            
            if (formData.frequency === 'Weekly' && (!formData.weeksCount || formData.weeksCount < 1)) {
                toast.error('Please enter valid number of weeks');
                return false;
            }
            
            if (formData.frequency === 'Monthly' && (!formData.monthsCount || formData.monthsCount < 1)) {
                toast.error('Please enter valid number of months');
                return false;
            }
        } else {
            if (!formData.dueDate) {
                toast.error('Due date is required for one-time tasks');
                return false;
            }
        }
        
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setSaving(true);
        try {
            const submitData = {
                title: formData.title,
                description: formData.description,
                assignees: selectedAssignees.map(a => a.mobile), // Send array of assignee mobiles
                department: formData.department || null,
                priority: formData.priority,
                frequency: formData.frequency,
                language: formData.language,
                dueDate: formData.frequency === 'One Time' ? formData.dueDate : null,
                startDate: formData.frequency !== 'One Time' ? formData.startDate : null,
                daysCount: formData.frequency === 'Daily' ? formData.daysCount : null,
                dayOfWeek: formData.frequency === 'Weekly' ? formData.dayOfWeek : null,
                weeksCount: formData.frequency === 'Weekly' ? formData.weeksCount : null,
                dayOfMonth: formData.frequency === 'Monthly' ? formData.dayOfMonth : null,
                monthsCount: formData.frequency === 'Monthly' ? formData.monthsCount : null
            };
            
            await axios.put(`/api/tasks/${taskId}`, submitData);
            toast.success(`Task updated and assigned to ${selectedAssignees.length} user(s)!`);
            navigate('/tasks');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update task');
        } finally {
            setSaving(false);
        }
    };

    const getFrequencyFields = () => {
        switch(formData.frequency) {
            case 'Daily':
                return (
                    <div className="col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <FaRedo className="inline mr-2" /> Number of Days
                        </label>
                        <input
                            type="number"
                            name="daysCount"
                            value={formData.daysCount}
                            onChange={handleInputChange}
                            placeholder="e.g., 7 (for 7 days)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            min="1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Task will repeat daily for this many days</p>
                    </div>
                );
            case 'Weekly':
                return (
                    <>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Day of Week</label>
                            <select
                                name="dayOfWeek"
                                value={formData.dayOfWeek}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="Monday">Monday</option>
                                <option value="Tuesday">Tuesday</option>
                                <option value="Wednesday">Wednesday</option>
                                <option value="Thursday">Thursday</option>
                                <option value="Friday">Friday</option>
                                <option value="Saturday">Saturday</option>
                                <option value="Sunday">Sunday</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Weeks</label>
                            <input
                                type="number"
                                name="weeksCount"
                                value={formData.weeksCount}
                                onChange={handleInputChange}
                                placeholder="e.g., 4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                min="1"
                            />
                        </div>
                    </>
                );
            case 'Monthly':
                return (
                    <>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Day of Month</label>
                            <select
                                name="dayOfMonth"
                                value={formData.dayOfMonth}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            >
                                {[...Array(31)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Months</label>
                            <input
                                type="number"
                                name="monthsCount"
                                value={formData.monthsCount}
                                onChange={handleInputChange}
                                placeholder="e.g., 3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                min="1"
                            />
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="loader"></div>
            </div>
        );
    }

    // Get available users (not already assigned)
    const availableUsers = users.filter(user => 
        !selectedAssignees.some(assigned => assigned.mobile === user.mobile)
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Edit Task</h1>
                        <p className="text-gray-500 mt-1">Update task details and assign to multiple users</p>
                    </div>
                    <button
                        onClick={() => navigate('/tasks')}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2"
                    >
                        <FaTimes /> Cancel
                    </button>
                </div>

                {/* Edit Form */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Information Section */}
                        <div className="border-b border-gray-200 pb-4">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Task Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter task title"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="4"
                                        placeholder="Enter task description"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Multiple Assignees Section */}
                        <div className="border-b border-gray-200 pb-4">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">
                                <FaUsers className="inline mr-2" /> Assignees <span className="text-red-500">*</span>
                            </h2>
                            
                            {/* Current Assignees List */}
                            {selectedAssignees.length > 0 && (
                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Assigned Users ({selectedAssignees.length})
                                    </label>
                                    <div className="space-y-2">
                                        {selectedAssignees.map(assignee => (
                                            <div key={assignee.mobile} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                                        <FaUserCheck className="text-green-600" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-gray-900">{assignee.name}</div>
                                                        <div className="text-sm text-gray-500">
                                                            {assignee.mobile}
                                                            {assignee.department && ` • ${assignee.department}`}
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveAssignee(assignee.mobile)}
                                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Remove Assignee"
                                                >
                                                    <FaTrash size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Add New Assignee */}
                            <div className="flex gap-3">
                                <div className="flex-1">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Add New Assignee
                                    </label>
                                    <select
                                        value={newAssignee}
                                        onChange={(e) => setNewAssignee(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    >
                                        <option value="">Select user to assign</option>
                                        {availableUsers.map(user => (
                                            <option key={user.id} value={user.mobile}>
                                                {user.name} - {user.mobile} {user.department ? `(${user.department})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <button
                                        type="button"
                                        onClick={handleAddAssignee}
                                        disabled={!newAssignee}
                                        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all flex items-center gap-2 disabled:opacity-50"
                                    >
                                        <FaPlus /> Add
                                    </button>
                                </div>
                            </div>
                            
                            {selectedAssignees.length === 0 && (
                                <p className="text-xs text-red-500 mt-2">Please assign at least one user to this task</p>
                            )}
                        </div>

                        {/* Task Configuration Section */}
                        <div className="border-b border-gray-200 pb-4">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Task Configuration</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        <FaBuilding className="inline mr-2" /> Department
                                    </label>
                                    <input
                                        type="text"
                                        name="department"
                                        value={formData.department}
                                        onChange={handleInputChange}
                                        placeholder="Department"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        list="departmentsList"
                                    />
                                    <datalist id="departmentsList">
                                        {departments.map((dept, idx) => (
                                            <option key={idx} value={dept} />
                                        ))}
                                    </datalist>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        <FaFlag className="inline mr-2" /> Priority
                                    </label>
                                    <select
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        <FaLanguage className="inline mr-2" /> Language
                                    </label>
                                    <select
                                        name="language"
                                        value={formData.language}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    >
                                        <option value="english">English</option>
                                        <option value="hindi">Hindi</option>
                                        <option value="gujarati">Gujarati</option>
                                        <option value="marathi">Marathi</option>
                                        <option value="tamil">Tamil</option>
                                        <option value="telugu">Telugu</option>
                                        <option value="bengali">Bengali</option>
                                        <option value="kannada">Kannada</option>
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        <FaRedo className="inline mr-2" /> Frequency
                                    </label>
                                    <select
                                        name="frequency"
                                        value={formData.frequency}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    >
                                        <option value="One Time">One Time</option>
                                        <option value="Daily">Daily</option>
                                        <option value="Weekly">Weekly</option>
                                        <option value="Monthly">Monthly</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Schedule Section */}
                        <div className="border-b border-gray-200 pb-4">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Schedule</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {formData.frequency === 'One Time' ? (
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            <FaCalendarAlt className="inline mr-2" /> Due Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            name="dueDate"
                                            value={formData.dueDate}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                            required={formData.frequency === 'One Time'}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                <FaCalendarAlt className="inline mr-2" /> Start Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={formData.startDate}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                                required={formData.frequency !== 'One Time'}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Tasks will start from this date</p>
                                        </div>
                                        {getFrequencyFields()}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <FaInfoCircle className="text-blue-500 text-lg mt-0.5" />
                                <div>
                                    <p className="text-sm text-blue-800 font-medium">Important Notes:</p>
                                    <ul className="text-xs text-blue-700 mt-1 space-y-1">
                                        <li>• Task will be assigned to all selected users</li>
                                        <li>• Each assignee will receive individual WhatsApp notifications</li>
                                        <li>• For recurring tasks, multiple tasks will be created for each assignee</li>
                                        <li>• Sundays and national holidays will be automatically skipped for recurring tasks</li>
                                        <li>• Each assignee can mark their own task as completed independently</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                disabled={saving || selectedAssignees.length === 0}
                                className="flex-1 bg-purple-500 text-white py-2 rounded-lg hover:bg-purple-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                <FaSave /> {saving ? 'Updating...' : `Update Task (${selectedAssignees.length} assignee${selectedAssignees.length !== 1 ? 's' : ''})`}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/tasks')}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-all flex items-center justify-center gap-2"
                            >
                                <FaTimes /> Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditTask;